'use client';

import {
  type AdminV2ParameterCalculatorResult,
  type AdminV2ParameterCalculatorScalar,
  createAdminV2ParameterCalculatorContextFingerprint,
  runAdminV2ParameterCalculator,
} from '@/admin-v2/tools/parameter-calculator';
import { Button } from '@/components/ui/actions/button';
import { useMemo, useState } from 'react';
import { buildWorkspaceParameterCalculatorRecommendations } from '../actions/parameter-calculator-runtime';
import { calculateWorkspaceRequiredCredits } from '../actions/runtime-utils';
import type {
  WorkspaceParameterCalculatorView,
  WorkspaceToolParamValue,
  WorkspaceToolView,
} from '../contracts/tool-view';
import type { WorkspaceStudioViewModel } from '../contracts/view-model';

type CalculatorContext = NonNullable<
  WorkspaceStudioViewModel['parameterCalculatorContext']
>;
type CalculatorCopy = NonNullable<
  WorkspaceStudioViewModel['shell']['controls']['calculatorCopy']
>;

type CalculatedState = {
  fingerprint: string;
  result: AdminV2ParameterCalculatorResult;
  recommendations: Record<string, WorkspaceToolParamValue>;
  projectedCredits: number | null;
};

function initialUserInputs(calculator: WorkspaceParameterCalculatorView) {
  return Object.fromEntries(
    calculator.inputBindings.flatMap((binding) =>
      binding.source.kind === 'userInput' &&
      binding.source.defaultValue !== undefined
        ? [[binding.inputId, binding.source.defaultValue]]
        : []
    )
  ) as Record<string, AdminV2ParameterCalculatorScalar>;
}

function formatValue(value: WorkspaceToolParamValue | undefined) {
  if (Array.isArray(value)) return value.map(String).join(', ');
  return value === undefined ? '—' : String(value);
}

function scalarKey(value: AdminV2ParameterCalculatorScalar) {
  return JSON.stringify(value);
}

function statusMessage(params: {
  calculated: CalculatedState | null;
  stale: boolean;
  calculator: WorkspaceParameterCalculatorView;
  copy: CalculatorCopy;
}) {
  if (params.stale) return params.copy.calculatorStaleMessage;
  const status = params.calculated?.result.status;
  if (!status) return null;
  if (status === 'incomplete') return params.copy.calculatorIncompleteMessage;
  if (status === 'unsupported') return params.copy.calculatorUnsupportedMessage;
  if (status === 'invalid') return params.copy.calculatorInvalidMessage;
  if (status === 'no-change') return params.copy.calculatorNoChangeMessage;
  const messageId = params.calculated?.result.messageId;
  return messageId ? params.calculator.copy[messageId] : null;
}

export function WorkspaceParameterCalculator({
  calculator,
  context,
  copy,
  tool,
  values,
  onApply,
}: {
  calculator: WorkspaceParameterCalculatorView;
  context: CalculatorContext;
  copy: CalculatorCopy;
  tool: WorkspaceToolView;
  values: Record<string, WorkspaceToolParamValue>;
  onApply: (
    recommendations: Record<string, WorkspaceToolParamValue>
  ) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [userInputs, setUserInputs] = useState(() =>
    initialUserInputs(calculator)
  );
  const [calculated, setCalculated] = useState<CalculatedState | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const fingerprint = useMemo(
    () =>
      createAdminV2ParameterCalculatorContextFingerprint({
        calculator,
        toolParams: values,
        userInputs,
        clientImageMeta: context.clientImageMeta,
        contextKey: context.contextKey,
        accessContextKey: context.accessContextKey,
        batchModeEnabled: context.batchModeEnabled,
        repeatGenerationCount: context.repeatGenerationCount,
      }),
    [calculator, context, userInputs, values]
  );
  const stale = Boolean(calculated && calculated.fingerprint !== fingerprint);
  const message = statusMessage({ calculated, stale, calculator, copy });
  const canApply = Boolean(
    calculated &&
      !stale &&
      (calculated.result.status === 'ready' ||
        calculated.result.status === 'warning')
  );
  const panelId = `workspace-calculator-${calculator.id}-panel`;

  const calculate = () => {
    setFeedback(null);
    try {
      const result = runAdminV2ParameterCalculator({
        instance: calculator,
        toolParams: values,
        userInputs,
        clientImageMeta: context.clientImageMeta,
      });
      const mapped = buildWorkspaceParameterCalculatorRecommendations({
        calculator,
        fields: tool.fields,
        currentValues: values,
        result,
      });
      if (
        (result.status === 'ready' || result.status === 'warning') &&
        !mapped.ok
      ) {
        setCalculated({
          fingerprint,
          result: { status: 'invalid', outputs: {}, displayItems: [] },
          recommendations: {},
          projectedCredits: null,
        });
        return;
      }
      const recommendations = mapped.ok ? mapped.recommendations : {};
      const finalResult =
        mapped.ok && mapped.noChange
          ? { ...result, status: 'no-change' as const }
          : result;
      const unitProjectedCredits =
        mapped.ok && !context.batchModeEnabled
          ? calculateWorkspaceRequiredCredits({
              tool,
              toolParams: { ...values, ...recommendations },
              hasPaidAccess: context.hasPaidAccess,
              inputImageUrl: context.inputImageUrl,
              prompt: context.prompt,
              referenceImageUrlsByFieldId: context.referenceImageUrlsByFieldId,
              clientMediaMeta:
                Object.keys(context.clientImageMeta).length > 0
                  ? { clientProvidedMeta: context.clientImageMeta }
                  : undefined,
            })
          : null;
      const projectedCredits =
        unitProjectedCredits === null
          ? null
          : unitProjectedCredits * Math.max(1, context.repeatGenerationCount);
      setCalculated({
        fingerprint,
        result: finalResult,
        recommendations,
        projectedCredits,
      });
    } catch (error) {
      console.error('[workspace] parameter calculator failed', error);
      setCalculated({
        fingerprint,
        result: { status: 'invalid', outputs: {}, displayItems: [] },
        recommendations: {},
        projectedCredits: null,
      });
    }
  };

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-controls={panelId}
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          {calculator.copy['entry.label']}
        </Button>
      </div>
    );
  }

  return (
    <section id={panelId} aria-label={calculator.copy['panel.title']}>
      <div className="grid w-full gap-3">
        <div>
          <div className="font-medium">{calculator.copy['panel.title']}</div>
          {calculator.copy['panel.description'] ? (
            <div className="text-muted-foreground text-sm">
              {calculator.copy['panel.description']}
            </div>
          ) : null}
        </div>
        {calculator.inputBindings.flatMap((binding) => {
          if (binding.source.kind !== 'userInput') return [];
          const source = binding.source;
          const label = calculator.copy[`input.${binding.inputId}.label`];
          const help = calculator.copy[`input.${binding.inputId}.help`];
          const unit = calculator.copy[`input.${binding.inputId}.unit`];
          const controlId = `workspace-calculator-${calculator.id}-${binding.inputId}`;
          const helpId = help ? `${controlId}-help` : undefined;
          return [
            <label
              className="grid gap-1.5"
              htmlFor={controlId}
              key={binding.inputId}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <span>{label}</span>
                {unit ? (
                  <span className="text-muted-foreground">{unit}</span>
                ) : null}
              </span>
              {source.control === 'number' ? (
                <input
                  id={controlId}
                  aria-label={label}
                  aria-describedby={helpId}
                  className="cms-block-workspace-workspace-1-workspace-form-controls__inputControl cms-block-workspace-workspace-1-workspace-form-controls__paramNumberInput"
                  min={source.min}
                  max={source.max}
                  required={source.required}
                  step={source.step}
                  type="number"
                  value={
                    typeof userInputs[binding.inputId] === 'number'
                      ? String(userInputs[binding.inputId])
                      : ''
                  }
                  onChange={(event) => {
                    setUserInputs((current) => {
                      const next = { ...current };
                      if (event.target.value === '') {
                        delete next[binding.inputId];
                      } else {
                        next[binding.inputId] = event.target.valueAsNumber;
                      }
                      return next;
                    });
                  }}
                />
              ) : (
                <select
                  id={controlId}
                  aria-label={label}
                  aria-describedby={helpId}
                  className="cms-block-workspace-workspace-1-workspace-form-controls__inputControl cms-block-workspace-workspace-1-workspace-form-controls__paramTextInput"
                  required={source.required}
                  value={
                    userInputs[binding.inputId] === undefined
                      ? ''
                      : scalarKey(userInputs[binding.inputId])
                  }
                  onChange={(event) => {
                    const option = source.options?.find(
                      (candidate) =>
                        scalarKey(candidate.value) === event.target.value
                    );
                    if (option) {
                      setUserInputs((current) => ({
                        ...current,
                        [binding.inputId]: option.value,
                      }));
                    }
                  }}
                >
                  <option value="" />
                  {source.options?.map((option) => (
                    <option
                      key={scalarKey(option.value)}
                      value={scalarKey(option.value)}
                    >
                      {
                        calculator.copy[
                          `input.${binding.inputId}.option.${String(option.value)}`
                        ]
                      }
                    </option>
                  ))}
                </select>
              )}
              {help ? (
                <span className="text-muted-foreground text-xs" id={helpId}>
                  {help}
                </span>
              ) : null}
            </label>,
          ];
        })}
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={calculate}>
            {calculated
              ? copy.calculatorRecalculateLabel
              : copy.calculatorCalculateLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setOpen(false)}
          >
            {copy.calculatorCloseLabel}
          </Button>
        </div>
        {calculated?.result.displayItems.length ? (
          <div className="grid gap-2" aria-live="polite">
            <div className="font-medium">{copy.calculatorResultTitle}</div>
            {calculated.result.displayItems.map((item) => {
              const mapping = calculator.outputMappings.find(
                (candidate) => candidate.outputId === item.outputId
              );
              const currentValue = mapping
                ? values[mapping.targetFieldId]
                : undefined;
              const recommendedValue = mapping
                ? calculated.recommendations[mapping.targetFieldId]
                : undefined;
              return (
                <div className="text-sm" key={item.id}>
                  {calculator.copy[`result.${item.id}.label`]}:{' '}
                  {formatValue(currentValue)} → {formatValue(recommendedValue)}
                </div>
              );
            })}
            {calculated.projectedCredits !== null ? (
              <div className="text-muted-foreground text-sm">
                {copy.calculatorEstimatedCreditsTemplate.replace(
                  '{credits}',
                  String(calculated.projectedCredits)
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        {message ? (
          <div className="text-muted-foreground text-sm" aria-live="polite">
            {message}
          </div>
        ) : null}
        {feedback ? (
          <div className="text-muted-foreground text-sm" aria-live="polite">
            {feedback}
          </div>
        ) : null}
        {calculated ? (
          <Button
            type="button"
            size="sm"
            disabled={!canApply}
            onClick={() => {
              if (!canApply || !calculated) return;
              if (!onApply(calculated.recommendations)) {
                setFeedback(copy.calculatorApplyFailedMessage);
                return;
              }
              setCalculated(null);
              setFeedback(copy.calculatorAppliedMessage);
            }}
          >
            {copy.calculatorApplyLabel}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
